import { CheckOutlined, ClockCircleOutlined, FileTextOutlined, RightOutlined } from '@ant-design/icons';
import ModuleCard from 'components/cards/ModuleCard';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import posthog from 'posthog-js';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { QuestionnaireResponseDTO } from 'services/api/models/QuestionnaireDTO';
import { routes } from 'services/RouteService';

export type MedicalProfileState = 'not_started' | 'in_progress' | 'pending_review' | 'in_review' | 'reviewed';

export function getMedicalProfileState(response: QuestionnaireResponseDTO | null | undefined): MedicalProfileState {
  if (!response) return 'not_started';
  if (!response.is_complete) return 'in_progress';
  if (response.reviewed_at) return 'reviewed';
  if (response.review_started_at) return 'in_review';
  return 'pending_review';
}

interface MedicalProfileCardProps {
  response: QuestionnaireResponseDTO | null | undefined;
}

const ROW_CLASS = 'flex items-center gap-3.5';
const ICON_CONTAINER_CLASS = 'flex size-11 shrink-0 items-center justify-center rounded-full bg-card/25';
const ICON_CLASS = 'text-2xl text-foreground';
const TEXT_CONTAINER_CLASS = 'min-w-0 flex-1';
const TITLE_CLASS = 'm-0 text-base leading-snug font-bold text-foreground';
const SUBTITLE_CLASS = 'mt-0.5 mb-0 text-xs leading-snug text-muted-foreground';
const ARROW_CLASS = 'shrink-0 text-subtle-foreground';

export default function MedicalProfileCard({ response }: MedicalProfileCardProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const state = getMedicalProfileState(response);

  const navigateToProfile = () => {
    posthog.capture('medical_profile.card_clicked', { card_state: state });
    navigate(routes.medicalProfile.route);
  };
  const handleKeyDown = (e: React.KeyboardEvent) => e.key === 'Enter' && navigateToProfile();

  if (state === 'pending_review') {
    return (
      <ModuleCard
        style={{ cursor: 'pointer' }}
        onClick={navigateToProfile}
        role="button"
        tabIndex={0}
        onKeyDown={handleKeyDown}
      >
        <div className={ROW_CLASS}>
          <div className={`${ICON_CONTAINER_CLASS} bg-info-muted`}>
            <ClockCircleOutlined className={ICON_CLASS} />
          </div>
          <div className={TEXT_CONTAINER_CLASS}>
            <div className="flex items-center gap-2">
              <p className={TITLE_CLASS}>{t('medicalProfile.homepage.pendingReview')}</p>
              <Badge variant="inverse">
                {t('medicalProfile.homepage.pendingReviewBadge')}
                <CheckOutlined className="ml-1" />
              </Badge>
            </div>
            <p className={`${SUBTITLE_CLASS} flex items-center gap-2`}>
              {t('medicalProfile.homepage.pendingReviewSubtitle')}
            </p>
          </div>
          <RightOutlined className={ARROW_CLASS} />
        </div>
      </ModuleCard>
    );
  }

  if (state === 'in_review') {
    return (
      <ModuleCard
        style={{ cursor: 'pointer' }}
        onClick={navigateToProfile}
        role="button"
        tabIndex={0}
        onKeyDown={handleKeyDown}
      >
        <div className={ROW_CLASS}>
          <div className={`${ICON_CONTAINER_CLASS} bg-info-muted`}>
            <FileTextOutlined className={ICON_CLASS} />
          </div>
          <div className={TEXT_CONTAINER_CLASS}>
            <p className={TITLE_CLASS}>{t('medicalProfile.homepage.inReview')}</p>
            <p className={SUBTITLE_CLASS}>{t('medicalProfile.homepage.inReviewSubtitle')}</p>
          </div>
          <RightOutlined className={ARROW_CLASS} />
        </div>
      </ModuleCard>
    );
  }

  if (state === 'reviewed') {
    return (
      <ModuleCard
        style={{ cursor: 'pointer' }}
        onClick={navigateToProfile}
        role="button"
        tabIndex={0}
        onKeyDown={handleKeyDown}
      >
        <div className={ROW_CLASS}>
          <div className={`${ICON_CONTAINER_CLASS} bg-info-muted`}>
            <FileTextOutlined className={ICON_CLASS} />
          </div>
          <div className={TEXT_CONTAINER_CLASS}>
            <p className={TITLE_CLASS}>{t('medicalProfile.homepage.reviewed')}</p>
            <p className={SUBTITLE_CLASS}>{t('medicalProfile.homepage.viewProfile')}</p>
          </div>
          <RightOutlined className={ARROW_CLASS} />
        </div>
      </ModuleCard>
    );
  }

  if (state === 'not_started') {
    return (
      <ModuleCard
        accentBackground
        style={{ cursor: 'pointer' }}
        onClick={navigateToProfile}
        role="button"
        tabIndex={0}
        title={<span className="text-primary-foreground">{t('medicalProfile.homepage.notStartedTitle')}</span>}
        onKeyDown={handleKeyDown}
      >
        <div className="flex flex-col gap-3 pt-3">
          <div className="flex items-center justify-start gap-3.5">
            <div className="flex size-10 shrink-0 items-center justify-center rounded-md bg-card/[0.18]">
              <FileTextOutlined className="text-xl text-inverse-foreground" />
            </div>
            <div className="flex items-center gap-2">
              {/*
               * `success` pairs the mint ground with the text-weight mint: the
               * solid mint fill is 2.16:1 as a label on it, the text form 5.14:1.
               */}
              <Badge variant="success">{t('medicalProfile.homepage.notStartedBadge')}</Badge>
              {/*
               * On `gradient.primary`, so it takes that gradient's `on` colour
               * (`primary-foreground`): the supporting-text tier measures 3.59:1
               * at the gradient's turquoise end, `on` 7.26:1 at its worst point.
               */}
              <span className="flex items-center gap-1 text-xs text-primary-foreground">
                <ClockCircleOutlined />
                {t('medicalProfile.homepage.duration')}
              </span>
            </div>
          </div>
          <p className="m-0 text-sm leading-[1.4] text-primary-foreground">
            {t('medicalProfile.homepage.notStartedSubtitle')}
          </p>
          <Button
            size="lg"
            onClick={navigateToProfile}
            /*
             * An opaque plate, not a tint of the foreground.
             *
             * This button sits on the brand gradient, which spans nearly the full
             * lightness range — no single label colour is readable across it. A
             * translucent wash let the gradient through and the label landed at
             * 1.8:1 over the turquoise stop. An opaque surface makes the pairing
             * independent of whatever is behind it.
             */
            className="bg-accent text-accent-foreground hover:bg-accent/90"
          >
            {t('medicalProfile.homepage.notStartedCta')}
          </Button>
        </div>
      </ModuleCard>
    );
  }

  // in_progress — show CTA
  return (
    <ModuleCard
      accentBackground
      style={{ cursor: 'pointer' }}
      onClick={navigateToProfile}
      role="button"
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      <div className={ROW_CLASS}>
        <div className={ICON_CONTAINER_CLASS}>
          <FileTextOutlined className="text-2xl text-primary-foreground" />
        </div>
        <div className={TEXT_CONTAINER_CLASS}>
          <p className={`${TITLE_CLASS} text-primary-foreground`}>{t('medicalProfile.homepage.cta')}</p>
          <p className={`${SUBTITLE_CLASS} text-primary-foreground`}>{t('medicalProfile.homepage.ctaDescription')}</p>
        </div>
        <RightOutlined className="shrink-0 text-primary-foreground" />
      </div>
    </ModuleCard>
  );
}
